import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { t } from '@/utils/i18n-helper';
import { useRouter, useRoute } from '@/composables/common/router/useRouter';

/**
 * Login form state and submit.
 *
 * Deps are injectable so the redirect logic can be unit tested without a
 * mounted component. When omitted they resolve from the current instance,
 * so this must be called from setup().
 */
export function useLogin(deps = {}) {
    const router = deps.router || useRouter();
    const route = deps.route || useRoute();

    const formData = ref({
        email: '',
        password: ''
    });

    const loading = ref(false);
    const mensaje = ref(null);
    const captchaToken = ref(null);
    const sitekey = process.env.VUE_APP_CLOUDFLARE_SITE_KEY;

    const footer = computed(() => store.getters.getFooter);

    const canSubmit = computed(() => !!captchaToken.value && !loading.value);

    const onCaptchaVerified = token => {
        captchaToken.value = token;
    };

    /**
     * Turnstile tokens expire while the user is still typing, and the widget
     * also reports hard errors. Both must invalidate the stored token, or
     * canSubmit would stay true and the form would submit a stale token.
     */
    const onCaptchaInvalid = () => {
        captchaToken.value = null;
    };

    /**
     * Turnstile tokens are single-use: once the backend has consumed one it
     * can never be reused. Login is the form where failed attempts are
     * routine, so every failure must hand the user a fresh token or they get
     * stuck unable to retry. The widget owns the token, hence the ref.
     */
    const resetCaptcha = captchaRef => {
        captchaToken.value = null;
        if (captchaRef && typeof captchaRef.reset === 'function') {
            captchaRef.reset();
        }
    };

    /**
     * An internal path is only usable when it is a single string starting with
     * '/'. A repeated query param (`?redirect=/a&redirect=/b`) makes Vue Router
     * hand over an array, and calling startsWith on it would throw AFTER the
     * login was already accepted, surfacing as a bogus "wrong credentials"
     * message.
     */
    const resolveInternalRedirect = () => {
        const redirect = route.query.redirect;

        if (typeof redirect !== 'string') return null;

        // The URL parser normalises backslashes into slashes, so '/\evil.com'
        // resolves to an external origin. A literal prefix check cannot catch
        // that, hence the explicit backslash rejection below.
        if (redirect.includes('\\')) return null;
        if (!redirect.startsWith('/')) return null;
        // Reject protocol-relative URLs ('//evil.com'), which also start with '/'.
        if (redirect.startsWith('//')) return null;

        try {
            const target = new URL(redirect, window.location.origin);
            if (target.origin !== window.location.origin) return null;
        } catch (error) {
            return null;
        }

        return redirect;
    };

    const goToDestination = data => {
        const user = data && data.user;
        const redirect = resolveInternalRedirect();

        if (user && user.tipo != 'user') {
            router.push({ name: 'back.events.index' });
        } else if (redirect) {
            router.push(redirect);
        } else if (window.location.href.includes('preventa')) {
            router.go(-1);
        } else {
            router.push({ name: 'perfil' });
        }
    };

    const submit = async captchaRef => {
        if (!canSubmit.value) return { ok: false };

        loading.value = true;
        mensaje.value = null;

        const payload = {
            ...formData.value,
            filters: {
                tid: store.getters.getConfig && store.getters.getConfig.hash_teatro
            },
            captchaToken: captchaToken.value
        };

        try {
            const res = await store.dispatch(config.getVuexAlias('login'), payload);

            store.commit('setFrontEvents', null);
            store.commit('setFrontExperiencias', null);

            goToDestination(res.data);

            return { ok: true, data: res.data };
        } catch (error) {
            // User-visible copy belongs to i18n: the composable must not pick a
            // language. The generic credentials message is kept as-is, only its
            // source changes from a literal to a translation key.
            mensaje.value = t('login.credenciales_incorrectas');
            resetCaptcha(captchaRef);
            return { ok: false, error };
        } finally {
            loading.value = false;
        }
    };

    return {
        formData,
        loading,
        mensaje,
        captchaToken,
        sitekey,
        footer,
        canSubmit,
        onCaptchaVerified,
        onCaptchaInvalid,
        resetCaptcha,
        resolveInternalRedirect,
        submit
    };
}
